import { InputBaseComponentProps, Stack } from '@mui/material';
import {
  ChangeEventHandler,
  cloneElement,
  CSSProperties,
  FocusEventHandler,
  MouseEventHandler,
  KeyboardEventHandler,
  ReactElement,
  useState
} from 'react';
import { AgColor } from '../../Styles/AgColor';
import AgTypography from '../../Styles/AgTypography';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import { useIntl } from 'react-intl';

export interface AgTextFieldProps {
  id?: string;
  type?: 'email' | 'password' | 'number' | 'date' | 'text';
  required?: boolean;
  disabled?: boolean;
  label?: string;
  caption?: string | string[];
  value?: string | number;
  defaultValue?: string | number;
  autoComplete?: string;
  placeholder?: string;
  inputProps?: InputBaseComponentProps | undefined;
  InputLabelProps?: { shrink: boolean } | undefined;
  onChange?: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> | undefined;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement> | undefined;
  style?: CSSProperties | undefined;
  formStyle?: CSSProperties | undefined;
  labelStyle?: CSSProperties | undefined;
  captionStyle?: CSSProperties | undefined;
  error?: boolean;
  datepickerIconLeft?: boolean;
  startIcon?: ReactElement;
  dataTestId?: string;
  onSearch?: MouseEventHandler<HTMLButtonElement>;
  onBlur?: FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  optionalText?: boolean;
  maxLength?: number;
  unitLeft?: string;
  unitRight?: string;
  buttonRight?: ReactElement;
  helperText?: string;
}

const AgTextField = (
  {
    id,
    type = 'text',
    required,
    disabled,
    label,
    caption,
    value,
    defaultValue,
    autoComplete,
    placeholder = label ?? 'Placeholder',
    onChange,
    onKeyDown,
    style,
    formStyle,
    labelStyle,
    captionStyle,
    error,
    datepickerIconLeft,
    startIcon,
    dataTestId,
    onSearch,
    onBlur,
    optionalText,
    maxLength,
    unitLeft,
    unitRight,
    helperText,
    inputProps = {},
    buttonRight
  }: AgTextFieldProps) => {
  const [hoverBorder, setHoverBorder] = useState<boolean>(false);
  const [focusBorder, setFocusBorder] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const messages = useIntl().formatMessage;

  const unitPaddingLeftLength = unitLeft ? `calc(${unitLeft?.length}ch + 26px)` : 0;
  const unitPaddingRightLength = unitRight ? `calc(${unitRight?.length}ch + 26px)` : 0;

  const handleTogglePasswordVisibility = () => {
    setShowPassword((prev) => !prev); // Toggle the showPassword state
  };
  const handleHoverEnter: MouseEventHandler<HTMLInputElement> = () => {
    setHoverBorder(true);
  };

  const handleHoverLeave: MouseEventHandler<HTMLInputElement> = () => {
    setHoverBorder(false);
  };
  const handleFocus: FocusEventHandler<HTMLInputElement> = () => {
    setFocusBorder(true);
  };

  const handleBlur: FocusEventHandler<HTMLInputElement> = (e) => {
    setFocusBorder(false);
    if (onBlur) {
      onBlur(e);
    }
  };

  const renderCaptions = (caption?: string | string[]) => {
    if (typeof caption === 'string') {
      return (
        <AgTypography
          contentStyle={'AP.Assistive.Text.Regular'}
          id={id}
          style={{
            marginLeft: '0',
            color: error ? AgColor.errorMain : AgColor.grey7,
            ...captionStyle
          }}
          {...inputProps}
        >
          {caption}
        </AgTypography>
      );
    } else if (Array.isArray(caption)) {
      return (
        caption.map((item, index) => (
          <AgTypography
            contentStyle={'AP.Assistive.Text.Regular'}
            id={id}
            key={index}
            style={{
              marginLeft: '0',
              color: error ? AgColor.errorMain : AgColor.grey7,
              ...captionStyle
            }}
            {...inputProps}
          >
            {item}
          </AgTypography>
        ))
      );
    } else {
      return null;
    }
  };

  return (
    <AgTypography style={{ display: 'flex', flexDirection: 'column', ...formStyle }}>
      {Boolean(label || required || optionalText) && <AgTypography
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          height: '16px'
        }}
      >
        <label
          htmlFor={id}
          style={{
            fontSize: '12px',
            lineHeight: '16px',
            fontFamily: 'Roboto, sans-serif',
            color: AgColor.grey9,
            fontWeight: 500,
            ...labelStyle
          }}>
          {label}
        </label>
        {!required && optionalText && <AgTypography
          contentStyle={'AP.Assistive.Text.Regular'}
          message={messages({ id: 'STORYBOOK_OPTIONAL' })}
        />}
      </AgTypography>}
      <AgTypography
        style={{
          display: 'flex',
          justifyContent: 'center',
          position: 'relative',
          height: '50px',
          padding: '6px 0',
          boxSizing: 'border-box'
        }}
      >
        {unitLeft && <AgTypography
          contentStyle={'AP.Paragraph.Large.Regular'}
          style={{
            display: 'inline',
            position: 'absolute',
            left: '16px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: AgColor.grey7
          }}>
          {unitLeft}
        </AgTypography>}
        <input
          id={id}
          type={showPassword ? 'text' : type}
          disabled={disabled}
          required={required}
          value={value}
          onChange={onChange}
          onMouseEnter={handleHoverEnter}
          onMouseLeave={handleHoverLeave}
          onKeyDown={onKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          defaultValue={defaultValue}
          autoComplete={autoComplete}
          placeholder={placeholder}
          data-testid={dataTestId}
          maxLength={maxLength}
          style={{
            backgroundColor: disabled ? AgColor.grey3 : AgColor.grey1,
            border: '1px solid',
            borderColor: error ? AgColor.errorMain : (hoverBorder || focusBorder) ? AgColor.link1 : AgColor.grey4,
            borderRadius: '6px',
            width: '100%',
            padding: (type === 'password') ? '6px 40px 6px 12px' : '6px 12px',
            fontFamily: 'Inter, sans-serif',
            fontStyle: 'normal',
            fontWeight: 400,
            fontSize: '16px',
            lineHeight: '38px',
            alignItems: 'center',
            outline: 'none',
            paddingLeft:
            unitLeft
              ? unitPaddingLeftLength
              : startIcon
                ? '38px'
                : datepickerIconLeft
                  ? '30px'
                  : '16px',
            paddingRight:
            unitRight
              ? unitPaddingRightLength
              : '12px',
            ...style
          }}
          {...inputProps}
        />
        {buttonRight && (
          <div style={{
            display: 'inline',
            position: 'absolute',
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
          }}>
            {buttonRight}
          </div>
        )}
        {unitRight && <AgTypography
          contentStyle={'AP.Paragraph.Large.Regular'}
          style={{
            display: 'inline',
            position: 'absolute',
            right: '16px',
            top: '50%',
            transform: 'translateY(-50%)',
            color: AgColor.grey7
          }}>
          {unitRight}
        </AgTypography>}
        {startIcon && <AgTypography
          onClick={onSearch}
          style={{
            width: '20px',
            height: '20px',
            position: 'absolute',
            zIndex: 1,
            padding: '0 12px',
            left: '0',
            top: '50%',
            transform: 'translateY(-40%)',
            cursor: 'pointer'
          }}
        >
          {cloneElement(startIcon, { style: { height: '20px', width: '18px' } })}
        </AgTypography>}
        {(type === 'password') && (
          <span
            onClick={handleTogglePasswordVisibility}
            style={{
              position: 'absolute',
              right: '12px',
              transform: 'translateY(calc(50% - 6px))',
              cursor: 'pointer'
            }}
          >
            {showPassword ? <VisibilityOutlinedIcon sx={{ fontSize: '20px' }} /> :
              <VisibilityOffOutlinedIcon sx={{ fontSize: '20px' }} />}
          </span>
        )}
      </AgTypography>
      <Stack flexDirection='row' justifyContent='space-between' alignItems='center'>
        <span>{renderCaptions(caption)}</span>
        <AgTypography contentStyle={'AP.Assistive.Text.Regular'}>
          {helperText}
        </AgTypography>
      </Stack>
      {datepickerIconLeft &&
        (<style>
          {`
          input[type="date"]::-webkit-calendar-picker-indicator {
            position: absolute;
            left: 24px;
          }
        `}
        </style>)}
    </AgTypography>
  );
};

export default AgTextField;